"use client";

import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { BRAND_LOGO_WHITE, Icon } from "@/shared/ui";
import type { NavSection } from "../render/nav";

export type SiteHeaderCopy = {
  homeLabel: string;
  logoAlt: string;
  navLabel: string;
  nav: { id: NavSection; label: string }[];
  connect: string;
  menuOpen: string;
  menuClose: string;
  languageLabel: string;
};

export type SiteHeaderBarProps = {
  copy: SiteHeaderCopy;
  /** Sign-in opener in the bar, shown from 1200px. */
  signIn: ReactNode;
  /** Sign-in opener in the mobile menu. */
  mobileSignIn: ReactNode;
  /** Language switch in the bar, shown from 640px. */
  language: ReactNode;
  /** Language switch in the mobile menu, shown below 640px. */
  mobileLanguage: ReactNode;
};

const HEADER_HEIGHT = 72;

/**
 * The fixed header of the prototype: a glass background once the page
 * scrolls, scroll-spy on the section links and the mobile menu.
 */
export function SiteHeaderBar({ copy, signIn, mobileSignIn, language, mobileLanguage }: SiteHeaderBarProps) {
  const header = useRef<HTMLElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<NavSection | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy: the link of the section under the header is marked as current.
  useEffect(() => {
    const sections = copy.nav.map((item) => document.getElementById(item.id)).filter((section): section is HTMLElement => section !== null);
    let queued = false;
    const spy = () => {
      queued = false;
      const probe = HEADER_HEIGHT + window.innerHeight * 0.3;
      let found: NavSection | null = null;
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) found = section.id as NavSection;
      }
      setCurrent(found);
    };
    const queue = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(spy);
      }
    };
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    spy();
    return () => {
      window.removeEventListener("scroll", queue);
      window.removeEventListener("resize", queue);
    };
  }, [copy.nav]);

  const setMenu = useCallback((next: boolean) => setOpen(next), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenu(false);
        menuButton.current?.focus();
      }
    };
    const onClick = (event: globalThis.MouseEvent) => {
      if (header.current && event.target instanceof Node && !header.current.contains(event.target)) setMenu(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [open, setMenu]);

  useEffect(() => {
    const wide = window.matchMedia("(min-width:1200px)");
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMenu(false);
    };
    wide.addEventListener("change", onChange);
    return () => wide.removeEventListener("change", onChange);
  }, [setMenu]);

  // A section link or a control marked data-closes-menu (the sign-in opener) closes the menu.
  const onMenuClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest("a, [data-closes-menu]")) setMenu(false);
  };

  const links = copy.nav.map((item) => (
    <a key={item.id} href={`#${item.id}`} data-spy={item.id} aria-current={current === item.id ? "true" : undefined}>{item.label}</a>
  ));

  const className = ["top", scrolled ? "scrolled" : "", open ? "menu-open" : ""].filter(Boolean).join(" ");

  return (
    <header className={className} id="top" ref={header}>
      <div className="wrap top-in">
        {/* eslint-disable-next-line @next/next/no-img-element -- a fixed-size SVG wordmark */}
        <a className="brand" href="#hero" aria-label={copy.homeLabel}><img src={BRAND_LOGO_WHITE} width={105} height={28} alt={copy.logoAlt} /></a>
        <nav className="nav" aria-label={copy.navLabel}>{links}</nav>
        <div className="top-actions">
          {language}
          {signIn}
          <a className="btn btn-primary btn-sm" href="#connect">{copy.connect}</a>
          <button
            className="menu-btn"
            id="menuBtn"
            ref={menuButton}
            type="button"
            aria-expanded={open}
            aria-controls="mnav"
            aria-label={open ? copy.menuClose : copy.menuOpen}
            data-focus-fallback=""
            onClick={() => setMenu(!open)}
          >
            <Icon name="menu" className="ic i-open" />
            <Icon name="x" className="ic i-close" />
          </button>
        </div>
      </div>
      <div className="mnav" id="mnav" hidden={!open} onClick={onMenuClick}>
        <nav aria-label={copy.navLabel}>{links}</nav>
        <div className="mnav-lang"><span aria-hidden="true">{copy.languageLabel}</span>{mobileLanguage}</div>
        {mobileSignIn}
      </div>
    </header>
  );
}
